// NavigationListener.js
import { useEffect } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { setNavbarActiveItem, setNavbarHeader } from './redux/slices/navbar/navbar-header.slice'
import { corusFavIcon } from './asset/icon'
import { gtoLogoGreenDarkIcon } from './asset/icon'
import { notifyReferrerChange } from './hooks/useReferrerAppCode'

const setFavicon = (href) => {
  let link = document.querySelector("link[rel~='icon']")
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = href
}

const setAppTitleAndFavicon = () => {
  const appCode = sessionStorage.getItem('appCode')
  const corusCodeName = window.__RUNTIME_CONFIG__?.VITE_CORUS_CODENAME || ''
  const referrer = corusCodeName || sessionStorage.getItem('referrer')

  if (appCode?.toLowerCase() === 'gto') {
    document.title = 'GTO AlixPartners'
    setFavicon(gtoLogoGreenDarkIcon)
  } else if (referrer) {
    document.title = 'Corus | AlixPartners'
    setFavicon(corusFavIcon)
  }
}

const findLabelByHref = (config, pathname) => {
  if (!config) return null
  for (const item of config.items) {
    if (item.href && pathname.includes(item.href)) return item.label
    if (item.groups) {
      for (const group of item.groups) {
        for (const sub of group.items || []) {
          if (sub.href && pathname.includes(sub.href)) return `${group.heading} / ${sub.label}`
        }
      }
    }
  }
  for (const setItem of config.additionalItems.settings) {
    if (setItem.href && pathname.includes(setItem.href)) {
      return setItem.label
    }
  }

  return null
}

//Finds the active route by matching pathname against the route list.
const findActiveRoute = (routes, pathname) => {
  if (!routes) return null

  for (let i = 0; i < routes.length; i++) {
    const route = routes[i]

    if (route.config_path && pathname.includes(route.config_path)) {
      return route
    }

    if (route.children) {
      const childMatch = findActiveRoute(route.children, pathname)
      if (childMatch) return childMatch
    }
  }

  return null
}

const createRouteMap = (allRouteList = []) => {
  return new Map(allRouteList.map((r) => [r.nav_id, r]))
}

export const getTopParentLabel = (activeItem, allRouteList) => {
  if (!activeItem) return ''

  const routeMap = createRouteMap(allRouteList)
  let current = activeItem

  while (current?.parent_nav_id && routeMap?.has(current.parent_nav_id)) {
    current = routeMap.get(current.parent_nav_id)
  }

  return current?.config_label ?? ''
}

const NavigationListener = () => {
  const location = useLocation()
  const navigationType = useNavigationType()
  const dispatch = useDispatch()
  const { routeListData, allRouteList } = useSelector((state) => state.routeData)
  const { activeVersion } = useSelector((state) => state.navMenuConfig)
  const fullUrl = window.location.href

  useEffect(() => {
    // Save the *previous* route before this one
    const prevRoute = sessionStorage.getItem('currentRoute')

    if (prevRoute) {
      sessionStorage.setItem('lastVisitedRoute', prevRoute)
    }

    // Always update current route
    sessionStorage.setItem('currentRoute', location.pathname)
  }, [location, navigationType])

  useEffect(() => {
    // Try URL query param first (?referrer=corus)
    const match = fullUrl.match(/[?&]referrer=([^&#?]+)/i)
    const matchCodeName = fullUrl.match(/[?&]corusreferrer=([^&#?]+)/i)
    const matchDomainName = fullUrl.match(/[?&]corusdomain=([^&#?]+)/i)
    const matchAppCode = fullUrl.match(/[?&]appcode=([^&#?]+)/i)

    const isCorusReferrer = match?.[1]?.toLowerCase() === 'corus'
    const isGtoAppCode = matchAppCode?.[1]?.toLowerCase() === 'gto'

    const clearReferrer = () => {
      sessionStorage.removeItem('referrer')
      sessionStorage.removeItem('referrerCorus')
      sessionStorage.removeItem('referrerDomain')
    }

    if (isCorusReferrer && isGtoAppCode) {
      sessionStorage.setItem('appCode', 'gto')
      clearReferrer()
    } else if (match && !matchAppCode) {
      if (isCorusReferrer) {
        sessionStorage.setItem('referrer', 'corus')
        sessionStorage.removeItem('appCode')
        if (matchCodeName) sessionStorage.setItem('referrerCorus', matchCodeName[1])
        if (matchDomainName) sessionStorage.setItem('referrerDomain', matchDomainName[1])
      } else {
        const currentReferrer = sessionStorage.getItem('referrer')
        if (currentReferrer && currentReferrer.toLowerCase() !== 'corus') {
          clearReferrer()
        }
      }
    } else if (!match && matchAppCode) {
      if (isGtoAppCode) {
        sessionStorage.setItem('appCode', 'gto')
        clearReferrer()
      } else {
        const currentAppCode = sessionStorage.getItem('appCode')
        if (currentAppCode && currentAppCode.toLowerCase() !== 'gto') {
          sessionStorage.removeItem('appCode')
        }
      }
    }

    setAppTitleAndFavicon()
    notifyReferrerChange()
  }, [fullUrl])

  useEffect(() => {
    const activeName = findActiveRoute(allRouteList, location.pathname)
    function getLevel2Label(data, str) {
      for (const element of data) {
        const level2List = element.children || []

        for (const element of level2List) {
          const level2 = element

          // Match at level 2
          if (level2.config_label === str) {
            return level2.config_label
          }

          // Match at level 3 (using some → stops early)
          if ((level2.children || []).some((l3) => l3.config_label === str)) {
            return level2.config_label
          }
        }
      }

      return 'Home'
    }
    if (
      sessionStorage.getItem('referrer') &&
      sessionStorage.getItem('referrer').toLowerCase() === 'corus'
    ) {
      const label = findLabelByHref(activeVersion?.config, location.pathname)
      dispatch(setNavbarHeader(label ?? 'Home'))
    } else {
      if (activeName) {
        dispatch(setNavbarActiveItem(activeName))
        dispatch(setNavbarHeader(getLevel2Label(routeListData, activeName?.config_label)))
      } else {
        dispatch(setNavbarActiveItem(null))
        dispatch(setNavbarHeader('Home'))
      }
    }
  }, [location.pathname, allRouteList, routeListData, activeVersion])

  return null
}

export default NavigationListener
